/**
 * @one-payments/web-components - Browser Entry Point (IIFE Bundle)
 *
 * This file is used ONLY for the browser/CDN bundle (IIFE format).
 * It is completely separate from the existing ESM build and does NOT
 * affect React, Vue, Angular, or any other framework wrappers.
 *
 * Responsibilities:
 * - Safely register the <one-payment> custom element (if not already registered)
 * - Expose a minimal global window.OnePayments API
 * - Be safe if loaded multiple times (no hard crashes)
 *
 * Usage (CDN):
 * <script src="https://cdn.jsdelivr.net/npm/@one-payments/web-components@X.Y.Z/dist/one-payment.browser.iife.js"></script>
 * <one-payment amount="5000" currency="USD" order-id="order-123"></one-payment>
 * <script>
 *   const el = document.querySelector('one-payment');
 *   el.config = { apiKey: 'pk_...', environment: 'prod' };
 *   el.adapters = OnePayments.createWebAdapters();
 * </script>
 *
 * @module @one-payments/web-components/browser
 */

// =============================================================================
// Imports
// These will be bundled inline by Vite/Rollup for the IIFE build
// =============================================================================

// Import the component class (NOT auto-registered in this context because
// we import the class directly, the @customElement decorator only fires
// when the module containing the class definition is evaluated)
import { OnePayment } from './one-payment.js';

// Import web adapters factory - this is the key API for CDN consumers
import { createWebAdapters } from '@one-payments/adapters-web';

// =============================================================================
// Constants
// =============================================================================

const TAG_NAME = 'one-payment';

// Version will be replaced at build time via Vite's define config
const VERSION = '__VERSION__';

// =============================================================================
// Safe Custom Element Registration
// =============================================================================

/**
 * Safely register the <one-payment> custom element.
 *
 * This function:
 * - Checks if customElements API is available
 * - Checks if the element is already registered (prevents errors)
 * - Wraps registration in try-catch for safety
 *
 * @returns {boolean} true if registration succeeded or element was already registered
 */
function registerElement(): boolean {
  // Check if customElements API is available (not available in Node.js/SSR)
  if (typeof customElements === 'undefined') {
    console.error(
      '[OnePayments] customElements API is not available in this environment. ' +
        'This bundle is designed for browser environments only.'
    );
    return false;
  }

  // Check if element is already registered (by ESM import, another bundle load, etc.)
  if (customElements.get(TAG_NAME)) {
    console.info(
      `[OnePayments] <${TAG_NAME}> is already registered. Skipping re-registration.`
    );
    return true;
  }

  // Attempt to register the custom element
  try {
    customElements.define(TAG_NAME, OnePayment);
    console.info(
      `[OnePayments] Registered <${TAG_NAME}> custom element (v${VERSION})`
    );
    return true;
  } catch (error) {
    console.error(`[OnePayments] Failed to register <${TAG_NAME}>:`, error);
    return false;
  }
}

// Register immediately when script loads
// This is safe because customElements.define() doesn't need DOMContentLoaded
registerElement();

// =============================================================================
// Global API
// =============================================================================

/**
 * Minimal global API exposed on window.OnePayments for CDN consumers.
 *
 * This API is intentionally small and stable. Only add new properties
 * if absolutely necessary for CDN integrations.
 */
const OnePayments = {
  /**
   * Factory to create standard web adapters for browser environment.
   *
   * This is the primary way CDN consumers configure the component:
   * ```javascript
   * const adapters = OnePayments.createWebAdapters();
   * element.adapters = adapters;
   * ```
   */
  createWebAdapters,

  /**
   * The registered custom element tag name.
   * Always 'one-payment'.
   */
  TAG_NAME,

  /**
   * Runtime version of this package.
   * Useful for debugging and support.
   */
  VERSION,

  /**
   * Check if the custom element is registered.
   * @returns {boolean} true if <one-payment> is registered
   */
  isRegistered(): boolean {
    return (
      typeof customElements !== 'undefined' &&
      customElements.get(TAG_NAME) !== undefined
    );
  },

  /**
   * Wait for the custom element to be defined.
   * Useful if you need to ensure the element is ready before use.
   * @returns {Promise<CustomElementConstructor>}
   */
  whenDefined(): Promise<CustomElementConstructor> {
    if (typeof customElements === 'undefined') {
      return Promise.reject(
        new Error('customElements API is not available')
      );
    }
    return customElements.whenDefined(TAG_NAME);
  },
};

// =============================================================================
// Attach to Window
// =============================================================================

// TypeScript declaration for window.OnePayments
declare global {
  interface Window {
    OnePayments?: typeof OnePayments;
  }
}

// Attach to window, but don't overwrite if something already exists
// This handles the case where the bundle might be loaded twice
if (typeof window !== 'undefined') {
  if (!window.OnePayments) {
    window.OnePayments = OnePayments;
  } else {
    console.warn(
      '[OnePayments] window.OnePayments already exists. ' +
        'The bundle may have been loaded multiple times. Using existing reference.'
    );
  }
}

// =============================================================================
// Exports (for potential ESM usage of this file, though primarily IIFE)
// =============================================================================

export default OnePayments;
export { OnePayments, createWebAdapters, TAG_NAME, VERSION };
